// @flow

import React, {useEffect} from 'react';
import {makeStyles, Typography, CircularProgress} from '@material-ui/core';
import {Alert} from '@material-ui/lab';
import ChatGrid from './ChatGrid/ChatGrid';
import ContactProfile from './ContactProfile/ContactProfile';
import InboxGrid from './InboxGrid/InboxGrid';
import ConversationsGrid from './ConversationsGrid/ConversationsGrid';
import {useSetInitialConversationTags} from '../../utils/conversationTag';
import chatBackground from '../../../../../assets/img/chat/background.webp';
import configureChat from '../../../../../assets/img/chat/configure-chat.webp';

import {useStoreDevices} from '../../utils';
import CustomButton from '../../../../../elements/CustomButton/CustomButton';
import {colors} from '../../../../../assets/styles/colors';
import {useCurrentConversation} from '../../utils/conversation';
import {useLocalStorage, useSessionStorage} from '../../../../../utils/dom';
import {useIsFeatureInTier} from '../../../../../utils/component';
import {useTierRestrictionActions} from '../../../../TierRestriction/utils';
import {setChatDeviceBehaviorService} from '../../promises';

const WHATSAPP_USERNAME_ALERT_DISMISSED_KEY =
  'multichat-whatsapp-username-alert-dismissed';

type Props = {};

const useStyles = makeStyles(theme => ({
  root: {
    height: 'calc(100vh - 104px)',
    width: '100%',
    position: 'relative',
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden',
    minHeight: 0,
    '& img': {
      width: '100%',
      height: '100%',
      maxWidth: '100%',
      maxHeight: '100%',
    },
  },
  paper: {
    flex: 1,
    minHeight: 0,
    width: '100%',
    background: '#FFFFFF',
    boxShadow:
      '0px 0px 1px rgba(40, 41, 61, 0.08), 0px 0.5px 2px rgba(96, 97, 112, 0.16)',
    borderRadius: '12px',
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden',
  },
  whatsappUsernameAlert: {
    flexShrink: 0,
    margin: theme.spacing(1, 1, 0, 1),
  },
  gridContainer: {
    flex: 1,
    minHeight: 0,
    overflow: 'hidden',
    display: 'grid',
    gridTemplateColumns: 'repeat(11, minmax(0, 1fr))',

    [theme.breakpoints.up('lg')]: {
      gridTemplateColumns: 'repeat(16, minmax(0, 1fr))',
    },
  },
  gridItem: {
    height: '100%',
    minHeight: 0,
    overflow: 'hidden',
  },

  inboxItem: {
    '&[data-expanded="true"]': {
      gridColumn: 'span 2',
    },
  },
  conversationItem: {
    gridColumn: 'span 4',

    '&[data-expanded="true"]': {
      gridColumn: 'span 3',
    },

    [theme.breakpoints.up('lg')]: {
      gridColumn: 'span 5',

      '&[data-expanded="true"]': {
        gridColumn: 'span 4',
      },
    },
  },
  chatItem: {
    gridColumn: 'span 5',

    [theme.breakpoints.up('lg')]: {
      gridColumn: 'span 9',

      '&[data-expanded="true"]': {
        gridColumn: 'span 6',
      },
    },
  },
  profileItem: {
    gridColumn: 'span 1',

    [theme.breakpoints.up('lg')]: {
      '&[data-expanded="true"]': {
        gridColumn: 'span 4',
      },
    },
  },

  card: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    maxWidth: 850,
    padding: '2.5rem',
    overflowX: 'hidden',
    position: 'absolute',
    background: 'white',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    boxShadow: 'rgba(0, 0, 0, 0.1) 0px 10px 50px',
    borderRadius: '1.5rem',
    overflow: 'hidden',
    aspectRatio: 1,

    '& img': {
      width: '464px',
      height: 'auto',
    },
  },
  loader: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    height: '100%',
    width: '100%',
    position: 'absolute',
    top: 0,
    left: 0,
    background: 'rgba(255, 255, 255, 0.8)',
    zIndex: 1,
  },
  image: {
    maxHeight: 220,
  },
  title: {
    fontWeight: 600,
    fontSize: 18,
    color: colors.grey900,
    margin: theme.spacing(2, 0),
  },
  button: {
    marginBottom: theme.spacing(2),
  },
}));

function MultiChat(props: Props) {
  const classes = useStyles();

  const [inboxExpanded, setInboxExpanded] = useLocalStorage(
    'INBOX_EXPANDED',
    false
  );
  const [profileExpanded, setProfileExpanded] = useLocalStorage(
    'PROFILE_EXPANDED',
    false
  );
  const [
    whatsappUsernameAlertDismissed,
    setWhatsappUsernameAlertDismissed,
  ] = useSessionStorage(WHATSAPP_USERNAME_ALERT_DISMISSED_KEY, false);

  const toggleInbox = () => setInboxExpanded(!inboxExpanded);
  const toggleProfile = () => setProfileExpanded(!profileExpanded);

  useSetInitialConversationTags();

  const {clearCurrentConversation} = useCurrentConversation();

  const {storeDevices} = useStoreDevices({
    refetchOnMount: true,
  });

  const deviceBehaviorTrackedRef = React.useRef([]);

  useEffect(() => {
    return () => {
      clearCurrentConversation();
    };
  }, [clearCurrentConversation]);

  useEffect(() => {
    const connectedWhatsappDevices = (storeDevices || []).filter(
      d => d.state === 'CONNECTED' && d.service === 'WHATSAPP_WEB'
    );

    connectedWhatsappDevices.forEach(device => {
      if (deviceBehaviorTrackedRef.current.includes(device.id)) return;

      deviceBehaviorTrackedRef.current = [
        ...deviceBehaviorTrackedRef.current,
        device.id,
      ];
      setChatDeviceBehaviorService(device.id, {
        service: 'WHATSAPP_WEB',
        deviceId: device.id,
        behavior: 'AVAILABLE',
      }).catch(console.error);
    });
  }, [storeDevices]);

  useEffect(() => {
    return () => {
      deviceBehaviorTrackedRef.current.forEach(deviceId => {
        setChatDeviceBehaviorService(deviceId, {
          service: 'WHATSAPP_WEB',
          deviceId,
          behavior: 'UNAVAILABLE',
        }).catch(console.error);
      });
    };
  }, []);

  return (
    <div className={classes.root}>
      <div className={classes.paper}>
        {!whatsappUsernameAlertDismissed && (
          <Alert
            severity="warning"
            className={classes.whatsappUsernameAlert}
            onClose={() => setWhatsappUsernameAlertDismissed(true)}
          >
            <span role="img" aria-label="anuncio">
              📢
            </span>{' '}
            WhatsApp está implementando los “nombres de usuario”. Si un cliente
            activa esta función, su chat aparecerá como una nueva conversación y
            no mostrará el historial anterior. Sugerimos editar su nombre para
            identificarlo. Pronto actualizaremos TiendaDA para relacionar los
            chat con el &quot;nombre de usuario&quot; y número.
          </Alert>
        )}
        <section className={classes.gridContainer}>
          <div
            className={`${classes.gridItem} ${classes.inboxItem}`}
            data-expanded={inboxExpanded}
          >
            <InboxGrid expanded={inboxExpanded} toggleExpanded={toggleInbox} />
          </div>
          <div
            className={`${classes.gridItem} ${classes.conversationItem}`}
            data-expanded={inboxExpanded}
          >
            <ConversationsGrid />
          </div>
          <div
            className={`${classes.gridItem} ${classes.chatItem}`}
            data-expanded={profileExpanded}
          >
            <ChatGrid />
          </div>
          <div
            className={`${classes.gridItem} ${classes.profileItem}`}
            data-expanded={profileExpanded}
          >
            <ContactProfile
              expanded={profileExpanded}
              toggleExpanded={toggleProfile}
            />
          </div>
        </section>
      </div>
    </div>
  );
}

export default function MultiChatWrapper(props: Props) {
  const {storeDevices, isLoading} = useStoreDevices({
    refetchOnMount: true,
  });

  const classes = useStyles();

  const hasTiendaChatFeature = useIsFeatureInTier('TIENDACHAT_WHATSAPP_WEB');

  const noDevices = !storeDevices || !storeDevices.length;
  const noDevicesConnected = storeDevices.every(
    device => device.state === 'ARCHIVED'
  );

  const {showTierRestrictionModal} = useTierRestrictionActions();

  const handleButtonClick = e => {
    if (hasTiendaChatFeature) return;
    e.preventDefault();
    showTierRestrictionModal();
  };

  if (noDevices || noDevicesConnected) {
    return (
      <div className={classes.root}>
        <img src={chatBackground} alt="bg-chat" />

        {isLoading && (
          <div className={classes.loader}>
            <CircularProgress size={60} />
          </div>
        )}

        <div className={classes.card}>
          <img
            src={configureChat}
            alt="configure chat"
            className={classes.image}
          />
          <Typography className={classes.title} align="center">
            ¡Antes de empezar debes
            <br /> configurar tu chat!
          </Typography>
          <CustomButton
            variant="contained"
            className={classes.button}
            onClick={handleButtonClick}
            to="/chat/configuration"
            disabled={isLoading}
          >
            Configurar
          </CustomButton>
        </div>
      </div>
    );
  }

  return <MultiChat {...props} />;
}
